import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Printer, Loader2 } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { shareReceiptImage } from "@/lib/whatsapp";

const MODE_LABEL = {
  cash: "Cash",
  upi: "UPI",
  card: "Card",
  credit: "Credit",
};

export default function PrintSaleReceipt() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const autoShare = params.get("share") === "whatsapp";
  const [sale, setSale] = useState(null);
  const [err, setErr] = useState(null);
  const [sharing, setSharing] = useState(false);
  const receiptRef = useRef(null);

  useEffect(() => {
    api
      .get(`/sales/${id}`)
      .then((r) => setSale(r.data))
      .catch((e) => {
        setErr(formatApiError(e));
        toast.error(formatApiError(e));
      });
  }, [id]);

  // Auto-print disabled on receipt page — users now choose Print or WhatsApp share manually.

  const onShare = async () => {
    if (!sale || !receiptRef.current) return;
    setSharing(true);
    try {
      await shareReceiptImage(sale, receiptRef.current, BRAND.name);
    } finally {
      setSharing(false);
    }
  };

  // Auto-trigger share once the receipt has rendered, when ?share=whatsapp is set.
  useEffect(() => {
    if (sale && autoShare) {
      const t = setTimeout(() => {
        onShare();
      }, 350);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sale, autoShare]);

  if (err) return <div className="p-8 text-red-700">{err}</div>;
  if (!sale) return <div className="p-8 text-muted-foreground">Loading receipt…</div>;

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #ffffff; }
        }
        .receipt { max-width: 360px; margin: 0 auto; padding: 16px; background: #ffffff; color: #222; font: 12px/1.4 'Courier New', monospace; }
        .receipt h1 { font-size: 18px; text-align: center; margin: 0 0 4px; letter-spacing: 0.06em; }
        .receipt .sub { text-align: center; font-size: 11px; color: #444; margin-bottom: 8px; }
        .receipt hr { border: none; border-top: 1px dashed #999; margin: 8px 0; }
        .receipt table { width: 100%; border-collapse: collapse; }
        .receipt th, .receipt td { padding: 2px 0; text-align: left; vertical-align: top; }
        .receipt th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; }
        .receipt .num { text-align: right; font-variant-numeric: tabular-nums; }
        .receipt .total { font-size: 14px; font-weight: 700; }
        .receipt .brand { text-align: center; font-size: 14px; font-weight: 700; letter-spacing: 0.1em; margin-bottom: 2px; }
        .receipt .brand-sub { text-align: center; font-size: 10px; color: #666; letter-spacing: 0.18em; text-transform: uppercase; margin-bottom: 6px; }
      `}</style>

      <div className="no-print flex justify-between items-center p-3 bg-[#FBF6F2] border-b">
        <Button variant="ghost" onClick={() => window.close()}>Close</Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={onShare}
            disabled={sharing}
            data-testid="sale-whatsapp-share"
            title={sale?.customer_snapshot?.phone1 ? `Send receipt image to ${sale.customer_snapshot.phone1}` : "Send receipt image via WhatsApp"}
          >
            {sharing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2" fill="#25D366" aria-hidden="true">
                <path d="M20.52 3.48A11.78 11.78 0 0012 0C5.4 0 .03 5.37.03 12c0 2.11.55 4.17 1.6 5.99L0 24l6.18-1.62A11.96 11.96 0 0012 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52zM12 21.82a9.79 9.79 0 01-5-1.37l-.36-.21-3.67.96.98-3.58-.23-.37A9.81 9.81 0 1121.82 12 9.83 9.83 0 0112 21.82zm5.36-7.34c-.29-.15-1.73-.85-2-.95-.27-.1-.46-.15-.66.15-.2.29-.76.95-.94 1.14-.17.2-.35.22-.64.07-.29-.15-1.23-.45-2.34-1.44-.86-.77-1.45-1.72-1.62-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.43-.51.15-.17.2-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.9-2.18-.24-.58-.48-.5-.66-.51h-.56c-.2 0-.51.07-.78.37-.27.29-1.02 1-1.02 2.43 0 1.43 1.04 2.81 1.18 3 .15.2 2.04 3.13 4.96 4.39.69.3 1.23.48 1.65.62.69.22 1.32.19 1.82.12.55-.08 1.73-.71 1.97-1.39.24-.68.24-1.27.17-1.39-.07-.12-.27-.2-.56-.34z" />
              </svg>
            )}
            {sharing ? "Preparing…" : "Send via WhatsApp"}
          </Button>
          <Button onClick={() => window.print()} style={{ backgroundColor: "#9E6D5B" }} data-testid="sale-print-trigger">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </div>
      </div>

      <div className="receipt" data-testid="sale-receipt" ref={receiptRef}>
        <div className="brand">{BRAND.name}</div>
        <div className="brand-sub">Cosmetic Back-Office</div>
        <hr />
        <div className="sub" style={{ textAlign: "left" }}>
          Receipt #{(sale.id || "").slice(0, 8).toUpperCase()}
          <br />
          Type: {sale.type?.toUpperCase()}
          {sale.customer_name && (
            <>
              <br />
              Customer: {sale.customer_name}
            </>
          )}
          {sale.customer_snapshot?.phone1 && (
            <>
              <br />
              Ph: {sale.customer_snapshot.phone1}
            </>
          )}
        </div>

        <hr />
        <table>
          <thead>
            <tr>
              <th style={{ width: "60%" }}>Item</th>
              <th className="num">Qty</th>
              <th className="num">MRP</th>
            </tr>
          </thead>
          <tbody>
            {(sale.line_items || []).map((l) => {
              const p = l.product_snapshot || {};
              const mrpLineTotal = Number(l.mrp || 0) * Number(l.quantity || 0);
              return (
                <tr key={l.id || `${p.product}-${l.quantity}-${l.mrp}`}>
                  <td>{p.product}</td>
                  <td className="num">{l.quantity}</td>
                  <td className="num">{mrpLineTotal.toFixed(2)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <hr />
        <table>
          <tbody>
            <tr>
              <td>Total (MRP)</td>
              <td className="num" data-testid="receipt-mrp-total">
                ₹{(sale.line_items || [])
                  .reduce((a, l) => a + Number(l.mrp || 0) * Number(l.quantity || 0), 0)
                  .toFixed(2)}
              </td>
            </tr>
            <tr>
              <td className="total">Amount after discount</td>
              <td className="num total" data-testid="receipt-final-amount">
                ₹{Number(sale.total || 0).toFixed(2)}
              </td>
            </tr>
            <tr>
              <td>Paid via</td>
              <td className="num">{MODE_LABEL[sale.payment_mode] || sale.payment_mode}</td>
            </tr>
            <tr>
              <td>Status</td>
              <td className="num">{(sale.payment_status || "").toUpperCase()}</td>
            </tr>
          </tbody>
        </table>

        {sale.notes && (
          <>
            <hr />
            <div style={{ fontSize: 11, whiteSpace: "pre-wrap" }}>{sale.notes}</div>
          </>
        )}

        <hr />
        <div style={{ textAlign: "center", fontSize: 11 }}>
          Served by {sale.created_by_name || "—"}
          <br />
          Thank you — visit again!
        </div>
      </div>
    </>
  );
}
